import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../../../api/apiClient';
import UserPageHeader from '../../components/UserPageHeader';

const ASSESSMENT_IMAGE = 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?auto=format&fit=crop&w=1200&q=80';

const AvailableAssessments = () => {
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await api.assessments.getUserAssessments();
        setAssessments(response.data.data || []);
        setSummary(response.data.summary || null);
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to load assessments');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const assignedCount = summary?.assigned_count ?? assessments.length;
  const attemptedCount = summary?.attempted_assessment_count ?? assessments.filter((item) => Number(item.attempt_count || 0) > 0).length;
  const passedCount = summary?.passed_count ?? assessments.filter((item) => item.latest_result?.result_status === 'pass').length;
  const failedCount = summary?.failed_count ?? assessments.filter((item) => item.latest_result?.result_status === 'fail').length;

  const formatScheduleDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-purple-600 border-t-transparent mb-4"></div>
          <p className="text-gray-600 text-lg font-medium">Loading assessments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 p-4 sm:p-6 md:p-8 lg:p-10">
      <UserPageHeader
        iconClass="fas fa-clipboard-check"
        title="Available Assessments"
        subtitle="Start assigned assessments and view your latest result status."
        badgeLabel={assignedCount > 0 ? `${assignedCount} Assigned` : null}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <div className="bg-white rounded-2xl border border-purple-100 shadow-md p-4 text-center">
          <p className="text-xs uppercase text-purple-500 mb-1">Assigned</p>
          <p className="text-2xl font-bold text-purple-700 mb-0">{assignedCount}</p>
        </div>
        <div className="bg-white rounded-2xl border border-blue-100 shadow-md p-4 text-center">
          <p className="text-xs uppercase text-blue-500 mb-1">Attempted</p>
          <p className="text-2xl font-bold text-blue-700 mb-0">{attemptedCount}</p>
        </div>
        <div className="bg-white rounded-2xl border border-emerald-100 shadow-md p-4 text-center">
          <p className="text-xs uppercase text-emerald-500 mb-1">Passed</p>
          <p className="text-2xl font-bold text-emerald-700 mb-0">{passedCount}</p>
        </div>
        <div className="bg-white rounded-2xl border border-red-100 shadow-md p-4 text-center">
          <p className="text-xs uppercase text-red-500 mb-1">Failed</p>
          <p className="text-2xl font-bold text-red-700 mb-0">{failedCount}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {assessments.length === 0 && (
          <div className="col-span-full bg-white rounded-2xl border border-purple-100 shadow-md p-8 text-center text-gray-600">
            No assessments have been assigned to you yet.
          </div>
        )}
        {assessments.map((item) => {
          const isUpcoming = item.schedule_status === 'upcoming' || item.can_start === false;
          const startLabel = formatScheduleDate(item.available_from);

          return (
          <div key={item.id} className="bg-white rounded-2xl border border-purple-100 shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden">
            <div className="h-40 overflow-hidden">
              <img
                src={ASSESSMENT_IMAGE}
                alt="Assessment learning"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between gap-2 mb-1">
                <h5 className="mb-0 text-gray-900">{item.title}</h5>
              {item.is_locked ? (
                <span className="inline-flex px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Passed</span>
              ) : item.latest_result ? (
                <span className="inline-flex px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                  {item.latest_result.result_status}
                </span>
              ) : isUpcoming ? (
                <span className="inline-flex px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">Scheduled</span>
              ) : (
                <span className="inline-flex px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">New</span>
              )}
              </div>
              <p className="text-sm text-gray-600 mb-2 min-h-10">{item.description || 'No description available'}</p>
              <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                <div className="bg-purple-50 rounded-lg p-2 text-purple-700">Total Questions: {item.question_count || 0}</div>
                <div className="bg-blue-50 rounded-lg p-2 text-blue-700">Total Attempts: {item.attempt_count || 0}</div>
              </div>
              {item.latest_result && (
                <p className="text-xs text-gray-700 mb-3">
                  Last Score: {item.latest_result.obtained_marks}/{item.latest_result.total_marks}
                </p>
              )}
              {startLabel && (
                <p className="text-xs text-gray-600 mb-3">
                  {isUpcoming ? `Starts: ${startLabel}` : `Available from: ${startLabel}`}
                  {item.available_to ? ` · Ends: ${formatScheduleDate(item.available_to)}` : ''}
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  bsPrefix="custom-hamburger"
                  className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm px-4 py-2"
                  disabled={isUpcoming || item.is_locked}
                  onClick={() => navigate(`/user/assessments/${item.id}`)}
                >
                  {item.is_locked ? 'Completed' : item.latest_result ? 'Retake' : isUpcoming ? 'Not Started Yet' : 'Start'}
                </Button>
                {item.latest_result && (
                  <Button
                    bsPrefix="custom-hamburger"
                    className="border-1 border-emerald-600 bg-white text-emerald-600 hover:from-emerald-700 hover:to-teal-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm px-4 py-2"
                    onClick={() => navigate(`/user/assessments/${item.id}/result`)}
                  >
                    View Result
                  </Button>
                )}
              </div>
            </div>
          </div>
        );
        })}
      </div>
    </div>
  );
};

export default AvailableAssessments;
